<script>
import { mapGetters } from 'vuex';
import { toast } from 'dashboard/composables/useToast';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SettingsTable from 'dashboard/components-next/Settings/SettingsTable.vue';
import googleAdsAPI from 'dashboard/api/googleAds';

export default {
  components: {
    SettingsTable,
    NextButton,
  },
  data() {
    return {
      connections: [],
      audiences: [],
      loading: false,
      creating: false,
      form: {
        name: '',
        connectionId: '',
        customerId: '',
        sourceType: 'all',
        sourceId: '',
      },
    };
  },
  computed: {
    ...mapGetters({
      segments: 'segments/getSegments',
      labels: 'labels/getLabels',
    }),
    sourceTypeOptions() {
      return [
        { value: 'all', label: this.$t('META_ADS.AUDIENCES.SOURCE.ALL') },
        {
          value: 'segment',
          label: this.$t('META_ADS.AUDIENCES.SOURCE.SEGMENT'),
        },
        { value: 'label', label: this.$t('META_ADS.AUDIENCES.SOURCE.LABEL') },
      ];
    },
    sourceRecords() {
      if (this.form.sourceType === 'segment') return this.segments;
      if (this.form.sourceType === 'label') return this.labels;
      return [];
    },
    canCreate() {
      const needsSource = this.form.sourceType !== 'all';
      return (
        this.form.name &&
        this.form.connectionId &&
        this.form.customerId &&
        (!needsSource || this.form.sourceId)
      );
    },
    // Contas da conexao escolhida: as marcadas na config, com o customer_id
    // legado como fallback. Um publico vive DENTRO de uma conta.
    accountOptions() {
      const conn = this.connections.find(c => c.id === this.form.connectionId);
      if (!conn) return [];
      const selected = conn.selected_ad_accounts || [];
      if (selected.length) return selected;
      if (conn.customer_id) {
        return [{ id: conn.customer_id, name: conn.name || conn.customer_id }];
      }
      return [];
    },
    tableHeaders() {
      return [
        this.$t('META_ADS.AUDIENCES.TABLE.NAME'),
        this.$t('META_ADS.AUDIENCES.TABLE.SOURCE'),
        this.$t('META_ADS.AUDIENCES.TABLE.COUNT'),
        this.$t('META_ADS.AUDIENCES.TABLE.STATUS'),
        this.$t('META_ADS.AUDIENCES.TABLE.LAST_SYNC'),
        this.$t('META_ADS.AUDIENCES.TABLE.ACTIONS'),
      ];
    },
  },
  mounted() {
    this.fetchConnections();
    this.fetchAudiences();
    this.$store.dispatch('segments/get');
    this.$store.dispatch('labels/get');
  },
  methods: {
    async fetchConnections() {
      try {
        const { data } = await googleAdsAPI.get();
        this.connections = (data || []).filter(
          c => c.customer_id || (c.selected_ad_accounts || []).length
        );
      } catch (error) {
        this.connections = [];
      }
    },
    // Trocou a conexao: reseta a conta (auto-escolhe quando so ha uma).
    onConnectionChange() {
      const opts = this.accountOptions;
      this.form.customerId = opts.length === 1 ? opts[0].id : '';
    },
    async fetchAudiences() {
      this.loading = true;
      try {
        const { data } = await googleAdsAPI.getAudiences();
        this.audiences = data;
      } finally {
        this.loading = false;
      }
    },
    connectionLabel(conn) {
      return conn.name || conn.customer_id || `#${conn.id}`;
    },
    goToConnections() {
      this.$router.push({
        query: { ...this.$route.query, tab: 'connections' },
      });
    },
    sourceLabel(audience) {
      if (audience.source_type === 'all') {
        return this.$t('META_ADS.AUDIENCES.SOURCE.ALL');
      }
      return audience.source_id;
    },
    statusLabel(status) {
      const labels = {
        active: this.$t('META_ADS.AUDIENCES.STATUS.ACTIVE'),
        error: this.$t('META_ADS.AUDIENCES.STATUS.ERROR'),
        pending: this.$t('META_ADS.AUDIENCES.STATUS.PENDING'),
      };
      return labels[status] || labels.pending;
    },
    lastSyncLabel(audience) {
      if (!audience.last_synced_at) return '—';
      return new Date(audience.last_synced_at).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    async createAudience() {
      this.creating = true;
      try {
        await googleAdsAPI.createAudience({
          name: this.form.name,
          google_ads_connection_id: this.form.connectionId,
          customer_id: this.form.customerId,
          source_type: this.form.sourceType,
          source_id: this.form.sourceType === 'all' ? '' : this.form.sourceId,
        });
        toast.success(this.$t('META_ADS.AUDIENCES.CREATE.SUCCESS'));
        this.form = {
          name: '',
          connectionId: '',
          customerId: '',
          sourceType: 'all',
          sourceId: '',
        };
        this.fetchAudiences();
      } catch (error) {
        toast.error(this.$t('META_ADS.AUDIENCES.CREATE.ERROR'));
      } finally {
        this.creating = false;
      }
    },
    async syncAudience(id) {
      try {
        await googleAdsAPI.syncAudience(id);
        toast.success(this.$t('META_ADS.AUDIENCES.SYNC.SUCCESS'));
        this.fetchAudiences();
      } catch (error) {
        toast.error(this.$t('META_ADS.AUDIENCES.SYNC.ERROR'));
      }
    },
    async deleteAudience(id) {
      try {
        await googleAdsAPI.deleteAudience(id);
        this.fetchAudiences();
      } catch (error) {
        toast.error(this.$t('META_ADS.AUDIENCES.DELETE.ERROR'));
      }
    },
  },
};
</script>

<template>
  <div class="flex flex-col w-full max-w-[60rem] gap-6 mx-auto">
    <div class="min-w-0">
      <h1 class="text-xl font-medium text-n-slate-12">
        {{ $t('META_ADS.GOOGLE_AUDIENCES.HEADER') }}
      </h1>
      <p class="mt-1 text-sm text-n-slate-10">
        {{ $t('META_ADS.GOOGLE_AUDIENCES.DESCRIPTION') }}
      </p>
      <p
        class="mt-2 inline-flex items-center gap-1.5 text-xs text-n-teal-11 bg-n-teal-3 rounded-lg px-2.5 py-1"
      >
        <span class="i-lucide-refresh-cw size-3" />
        {{ $t('META_ADS.AUDIENCES.EVERGREEN_INFO') }}
      </p>
    </div>

    <div
      v-if="!connections.length"
      class="flex flex-col items-start gap-3 p-4 text-sm rounded-lg text-n-slate-11 bg-n-alpha-black2"
    >
      {{ $t('META_ADS.GOOGLE_AUDIENCES.NO_CONNECTION') }}
      <NextButton
        icon="i-lucide-plug"
        :label="$t('META_ADS.CONNECTIONS.GO')"
        sm
        @click="goToConnections"
      />
    </div>

    <div v-else class="flex flex-col gap-4 p-5 rounded-xl bg-n-solid-2">
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label class="flex flex-col gap-1 text-sm text-n-slate-11">
          {{ $t('META_ADS.AUDIENCES.FORM.NAME') }}
          <input
            v-model="form.name"
            type="text"
            class="px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-black2 text-n-slate-12"
            :placeholder="$t('META_ADS.AUDIENCES.FORM.NAME_PLACEHOLDER')"
          />
        </label>
        <label class="flex flex-col gap-1 text-sm text-n-slate-11">
          {{ $t('META_ADS.GOOGLE_AUDIENCES.ACCOUNT') }}
          <select
            v-model="form.connectionId"
            class="px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-black2 text-n-slate-12"
            @change="onConnectionChange"
          >
            <option value="">
              {{ $t('META_ADS.AUDIENCES.FORM.SELECT') }}
            </option>
            <option v-for="conn in connections" :key="conn.id" :value="conn.id">
              {{ connectionLabel(conn) }}
            </option>
          </select>
        </label>
        <label class="flex flex-col gap-1 text-sm text-n-slate-11">
          {{ $t('META_ADS.AUDIENCES.FORM.AD_ACCOUNT') }}
          <select
            v-model="form.customerId"
            :disabled="!accountOptions.length"
            class="px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-black2 text-n-slate-12"
          >
            <option value="">
              {{ $t('META_ADS.AUDIENCES.FORM.SELECT') }}
            </option>
            <option v-for="acc in accountOptions" :key="acc.id" :value="acc.id">
              {{ acc.name || acc.id }}
            </option>
          </select>
        </label>
        <label class="flex flex-col gap-1 text-sm text-n-slate-11">
          {{ $t('META_ADS.AUDIENCES.FORM.SOURCE_TYPE') }}
          <select
            v-model="form.sourceType"
            class="px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-black2 text-n-slate-12"
          >
            <option
              v-for="opt in sourceTypeOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
        </label>
        <label
          v-if="form.sourceType !== 'all'"
          class="flex flex-col gap-1 text-sm text-n-slate-11"
        >
          {{ $t('META_ADS.AUDIENCES.FORM.SOURCE') }}
          <select
            v-model="form.sourceId"
            class="px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-black2 text-n-slate-12"
          >
            <option value="">
              {{ $t('META_ADS.AUDIENCES.FORM.SELECT') }}
            </option>
            <option
              v-for="record in sourceRecords"
              :key="record.id"
              :value="form.sourceType === 'label' ? record.title : record.id"
            >
              {{ form.sourceType === 'label' ? record.title : record.name }}
            </option>
          </select>
        </label>
      </div>
      <div class="flex justify-end">
        <NextButton
          icon="i-lucide-circle-plus"
          :label="$t('META_ADS.AUDIENCES.FORM.SUBMIT')"
          :is-loading="creating"
          :disabled="!canCreate"
          @click="createAudience"
        />
      </div>
    </div>

    <SettingsTable
      :headers="tableHeaders"
      :is-loading="loading"
      :is-empty="!audiences.length"
      :empty-message="$t('META_ADS.AUDIENCES.EMPTY')"
    >
      <tr v-for="audience in audiences" :key="audience.id">
        <td class="px-5 py-4 font-medium break-words text-n-slate-12">
          {{ audience.name }}
        </td>
        <td class="px-5 py-4 text-sm text-n-slate-11">
          {{ sourceLabel(audience) }}
        </td>
        <td class="px-5 py-4 text-sm text-n-slate-11">
          {{ audience.synced_count }}
        </td>
        <td class="px-5 py-4">
          <span
            class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
            :class="
              audience.status === 'active'
                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                : audience.status === 'error'
                  ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                  : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
            "
          >
            {{ statusLabel(audience.status) }}
          </span>
        </td>
        <td class="px-5 py-4 text-sm text-n-slate-10">
          {{ lastSyncLabel(audience) }}
        </td>
        <td class="px-5 py-4">
          <div class="flex justify-end gap-1">
            <NextButton
              v-tooltip.top="$t('META_ADS.AUDIENCES.SYNC.BUTTON')"
              icon="i-lucide-refresh-cw"
              xs
              slate
              faded
              @click="syncAudience(audience.id)"
            />
            <NextButton
              v-tooltip.top="$t('META_ADS.AUDIENCES.DELETE.BUTTON')"
              icon="i-lucide-trash-2"
              xs
              ruby
              faded
              @click="deleteAudience(audience.id)"
            />
          </div>
        </td>
      </tr>
    </SettingsTable>
  </div>
</template>
